import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_sharda-energy-tech/artifacts/akhfmdib_header%20logo.svg";

const NAV_LINKS = [
  { label: "About", id: "about" },
  { label: "Solutions", id: "solutions" },
  { label: "Capabilities", id: "manufacturing" },
  { label: "Industries", id: "industries" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-8 md:top-9 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#040D1C]/90 backdrop-blur-xl border-b border-[#003771]/40"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center group mr-10 lg:mr-16"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <img src={LOGO_URL} alt="Sharda Energy Tech" className="h-5 sm:h-6 lg:h-7 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5 lg:gap-1">
            {NAV_LINKS.map((link) => {
              const isContact = link.id === "contact";
              return (
                <button
                  key={link.id}
                  data-testid={`nav-${link.id}`}
                  onClick={() => scrollTo(link.id)}
                  className={`px-2.5 lg:px-4 py-2 text-[11px] lg:text-xs font-medium tracking-wide uppercase transition-colors duration-200 ${
                    isContact
                      ? "text-[#1E96FC] hover:text-[#1E96FC]/80"
                      : "text-slate-400 hover:text-[#1E96FC]"
                  }`}
                >
                  {link.label}
                </button>
              );
            })}
            <Link
              to="/sharda-group"
              data-testid="nav-sharda-group"
              className="ml-1 lg:ml-2"
            >
              <Button
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300 bg-transparent hover:bg-[#1E96FC] hover:border-[#1E96FC] hover:text-white uppercase tracking-widest text-[10px] lg:text-[11px] rounded-sm transition-colors"
              >
                Sharda Group
                <ArrowUpRight className="w-3 h-3 lg:w-3.5 lg:h-3.5 ml-1" />
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="nav-mobile-toggle"
                className="text-slate-300 hover:text-[#1E96FC]"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#040D1C] border-[#003771]/30 w-72"
            >
              <div className="flex flex-col gap-2 mt-8">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    data-testid={`mobile-nav-${link.id}`}
                    onClick={() => scrollTo(link.id)}
                    className="text-left px-4 py-3 text-base font-semibold text-slate-300 hover:text-[#1E96FC] hover:bg-[#003771]/20 rounded-sm uppercase tracking-wide transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
                <Link
                  to="/sharda-group"
                  data-testid="mobile-nav-sharda-group"
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-base font-semibold text-[#1E96FC] hover:bg-[#003771]/20 rounded-sm uppercase tracking-wide flex items-center gap-2"
                >
                  Sharda Group
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
